/**
 * useLeadKeyboardNavigation - Keyboard navigation for leads list
 *
 * Provides vim-style navigation (j/k) and quick actions (c/e/1-3).
 * Enabled by default as per user requirements.
 */

import { useEffect, useCallback, useState } from 'react'

interface UseLeadKeyboardNavigationOptions {
  leads: { id: string; customerPhone?: string | null; customerEmail?: string | null }[]
  stages: { id: string; isWon?: boolean; isLost?: boolean }[]
  enabled?: boolean
  onNavigate?: (leadId: string) => void
  onCall?: (leadId: string) => void
  onEmail?: (leadId: string) => void
  onStageChange?: (leadId: string, stageId: string) => void
  onMarkWon?: (leadId: string) => void
  onMarkLost?: (leadId: string) => void
  onOpenDetail?: (leadId: string) => void
}

interface KeyboardShortcut {
  key: string
  label: string
  description: string
  category: 'navigation' | 'actions' | 'stages'
}

export const KEYBOARD_SHORTCUTS: KeyboardShortcut[] = [
  // Navigation
  { key: 'j', label: 'j', description: 'Next lead', category: 'navigation' },
  { key: 'k', label: 'k', description: 'Previous lead', category: 'navigation' },
  { key: 'Enter', label: 'Enter', description: 'Open lead details', category: 'navigation' },
  { key: 'Escape', label: 'Esc', description: 'Deselect lead', category: 'navigation' },

  // Quick Actions
  { key: 'c', label: 'c', description: 'Call lead', category: 'actions' },
  { key: 'e', label: 'e', description: 'Email lead', category: 'actions' },

  // Stage Transitions
  { key: '1', label: '1', description: 'Move to stage 1', category: 'stages' },
  { key: '2', label: '2', description: 'Move to stage 2', category: 'stages' },
  { key: '3', label: '3', description: 'Move to stage 3', category: 'stages' },
  { key: 'w', label: 'w', description: 'Mark as Won', category: 'stages' },
  { key: 'l', label: 'l', description: 'Mark as Lost', category: 'stages' },

  // Help
  { key: '?', label: '?', description: 'Show keyboard shortcuts', category: 'navigation' },
]

export function useLeadKeyboardNavigation({
  leads,
  stages,
  enabled = true,
  onNavigate,
  onCall,
  onEmail,
  onStageChange,
  onMarkWon,
  onMarkLost,
  onOpenDetail,
}: UseLeadKeyboardNavigationOptions) {
  const [selectedIndex, setSelectedIndex] = useState<number>(-1)
  const [showHelp, setShowHelp] = useState(false)

  // Get active stages (not won/lost)
  const activeStages = stages.filter((s) => !s.isWon && !s.isLost)

  // Get selected lead
  const selectedLead = selectedIndex >= 0 && selectedIndex < leads.length
    ? leads[selectedIndex]
    : null

  // Reset selection when leads change
  useEffect(() => {
    setSelectedIndex(-1)
  }, [leads.length])

  // Notify parent of navigation
  useEffect(() => {
    if (selectedLead && onNavigate) {
      onNavigate(selectedLead.id)
    }
  }, [selectedLead?.id, onNavigate])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Don't capture if typing in an input
      const target = event.target as HTMLElement
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return
      }

      // Don't capture if modal is open (except for Escape)
      if (
        event.key !== 'Escape' &&
        document.querySelector('[role="dialog"]')
      ) {
        return
      }

      switch (event.key.toLowerCase()) {
        // Navigation
        case 'j':
          event.preventDefault()
          setSelectedIndex((prev) =>
            prev < leads.length - 1 ? prev + 1 : prev
          )
          break

        case 'k':
          event.preventDefault()
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev))
          break

        case 'enter':
          if (selectedLead && onOpenDetail) {
            event.preventDefault()
            onOpenDetail(selectedLead.id)
          }
          break

        case 'escape':
          event.preventDefault()
          setSelectedIndex(-1)
          setShowHelp(false)
          break

        // Quick Actions
        case 'c':
          if (selectedLead?.customerPhone && onCall) {
            event.preventDefault()
            onCall(selectedLead.id)
          }
          break

        case 'e':
          if (selectedLead?.customerEmail && onEmail) {
            event.preventDefault()
            onEmail(selectedLead.id)
          }
          break

        // Stage Transitions (1-3)
        case '1':
        case '2':
        case '3': {
          const stageIndex = parseInt(event.key, 10) - 1
          if (selectedLead && activeStages[stageIndex] && onStageChange) {
            event.preventDefault()
            onStageChange(selectedLead.id, activeStages[stageIndex].id)
          }
          break
        }

        // Mark Won/Lost
        case 'w':
          if (selectedLead && onMarkWon) {
            event.preventDefault()
            onMarkWon(selectedLead.id)
          }
          break

        case 'l':
          if (selectedLead && onMarkLost) {
            event.preventDefault()
            onMarkLost(selectedLead.id)
          }
          break

        // Help
        case '?':
          event.preventDefault()
          setShowHelp((prev) => !prev)
          break

        default:
          break
      }
    },
    [
      leads,
      selectedLead,
      activeStages,
      onCall,
      onEmail,
      onStageChange,
      onMarkWon,
      onMarkLost,
      onOpenDetail,
    ]
  )

  // Add/remove event listener
  useEffect(() => {
    if (!enabled) return

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [enabled, handleKeyDown])

  return {
    selectedIndex,
    selectedLead,
    setSelectedIndex,
    showHelp,
    setShowHelp,
    shortcuts: KEYBOARD_SHORTCUTS,
  }
}
